/**
 * Reviews.jsx
 * Links sent out for approval, and what came back.
 *
 * The list is arranged around the two questions that actually get asked: has
 * anyone looked at it, and what did they say. A link nobody has opened is
 * called that plainly, because "sent" and "read" are very different states and
 * conflating them is how a week goes by waiting on somebody who never got it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import ShareForReview from '../components/ShareForReview.jsx';
import { Users, Warning, Check, Plus } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

/** "facebook" -> "Facebook", for a row that has to read as a sentence. */
const nameOf = (platform) =>
  ({ facebook: 'Facebook', instagram: 'Instagram' })[platform] ?? platform;

const STATE_TONE = { live: 'ok', expired: 'muted', revoked: 'muted' };
const STATE_TEXT = { live: 'Live', expired: 'Expired', revoked: 'Withdrawn' };

export default function Reviews() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [open, setOpen] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [sharing, setSharing] = useState(false);
  const [posts, setPosts] = useState([]);
  const [chosen, setChosen] = useState([]);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const [links, drafts] = await Promise.all([
        api.reviews.list(brandId),
        api.posts.list({ brandId, status: 'draft' }),
      ]);
      setData(links);
      setPosts(drafts.posts ?? []);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
    setChosen([]);
  }, [load]);

  async function show(id) {
    if (open === id) {
      setOpen(null);
      return;
    }
    setOpen(id);
    if (feedback[id]) return;
    try {
      // Awaited before the updater runs: an `await` inside the setState
      // callback is not in an async function, and the callback must stay
      // synchronous anyway.
      const answers = await api.reviews.feedback(id);
      setFeedback((f) => ({ ...f, [id]: answers }));
    } catch (e) {
      setError(e);
    }
  }

  async function withdraw(id) {
    setError(null);
    try {
      const res = await api.reviews.revoke(id);
      setNote(res.message);
      await load();
    } catch (e) {
      setError(e);
    }
  }

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  const brand = brands.find((b) => b.id === brandId);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Approvals</div>
          <h1>Send posts to someone before they go out</h1>
          <p>
            A link they can open without an account. They approve, ask for changes or leave a note —
            and none of it publishes anything, because that stays with you.
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <button
            className="btn btn--primary"
            disabled={chosen.length === 0}
            onClick={() => setSharing(true)}
            title={chosen.length === 0 ? 'Choose some drafts below first' : ''}
          >
            <Plus /> Share {chosen.length > 0 ? `${chosen.length} draft${chosen.length === 1 ? '' : 's'}` : ''}
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--ok">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {/* ---------- pick what to send ---------- */}
      <section className="card">
        <div className="card__head">
          <div className="card__head-text">
            <h2>Drafts you could send</h2>
            <p>Choose the ones to share, then press Share above.</p>
          </div>
        </div>

        {posts.length === 0 ? (
          <p className="hint">No drafts for this brand.</p>
        ) : (
          <div className="rows">
            {posts.slice(0, 40).map((p) => {
              const on = chosen.includes(p.id);
              return (
                <label className={`row row--pick${on ? ' is-picked' : ''}`} key={p.id}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setChosen((c) => (on ? c.filter((id) => id !== p.id) : [...c, p.id]))
                    }
                  />
                  {/* The drafts list returns the first caption as `preview`,
                      not as a targets array — reading the wrong field made
                      every row say "No caption", which is exactly the
                      information needed to tell them apart. */}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="row__label">
                      {p.preview?.trim()
                        ? p.preview.trim().slice(0, 90)
                        : `Untitled draft — ${p.media_id ? 'image, no caption yet' : 'nothing written yet'}`}
                    </div>
                    <div className="row__sub">
                      {p.platforms
                        ? p.platforms.split(',').map(nameOf).join(' and ')
                        : 'No platforms chosen'}
                      {' · '}
                      {p.scheduled_at_utc
                        ? formatLondon(p.scheduled_at_utc)
                        : `written ${formatLondon(p.created_at)}`}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </section>

      {/* ---------- links already sent ---------- */}
      <section className="card">
        <div className="card__head">
          <span className="tile">
            <Users size={21} />
          </span>
          <div className="card__head-text">
            <h2>Links you have sent</h2>
            <p>{data ? `${data.links?.length ?? 0} in total.` : 'Loading…'}</p>
          </div>
        </div>

        {data?.links?.length === 0 && <p className="hint">Nothing sent out yet.</p>}

        <div className="rows">
          {(data?.links ?? []).map((link) => (
            <div className="row" key={link.id}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="row__label">{link.name || 'Untitled'}</div>
                <div className="row__sub">
                  {link.opens === 0 ? (
                    // "Sent" and "read" are very different states, and
                    // conflating them is how a week goes by waiting on someone
                    // who never got the link.
                    <b>Not opened yet</b>
                  ) : (
                    `Opened ${link.opens} time${link.opens === 1 ? '' : 's'}, last ${formatLondon(link.opened_at)}`
                  )}
                  {' · '}
                  {link.expiryNote}
                  {link.campaign_id ? ' · a whole campaign' : ` · ${link.posts} posts`}
                </div>
              </div>

              <div className="row__end">
                {link.answers > 0 && (
                  <>
                    {link.approved > 0 && <span className="badge badge--ok">{link.approved} approved</span>}
                    {link.changes > 0 && <span className="badge badge--warn">{link.changes} changes</span>}
                  </>
                )}
                <span className={`badge${STATE_TONE[link.state] === 'ok' ? ' badge--ok' : ''}`}>
                  {STATE_TEXT[link.state] ?? link.state}
                </span>
                <button className="btn btn--sm" onClick={() => show(link.id)}>
                  {open === link.id ? 'Hide' : 'What they said'}
                </button>
                {link.state === 'live' && (
                  <button className="btn btn--sm btn--danger" onClick={() => withdraw(link.id)}>
                    Withdraw
                  </button>
                )}
              </div>

              {open === link.id && (
                <div className="row__expand">
                  {!feedback[link.id] ? (
                    <p className="hint">Loading…</p>
                  ) : (
                    <>
                      <p className="hint">{feedback[link.id].summary.text}</p>
                      <div className="rows">
                        {feedback[link.id].posts.map((p) => (
                          <div className="row" key={p.id}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div className="row__label">
                                {(p.caption || 'No caption').slice(0, 80)}
                              </div>
                              {p.decision?.comment && (
                                <div className="row__sub">
                                  “{p.decision.comment}”
                                  {p.decision.who ? ` — ${p.decision.who}` : ''}
                                </div>
                              )}
                            </div>
                            <div className="row__end">
                              <span
                                className={`badge${
                                  p.decision?.decision === 'approved'
                                    ? ' badge--ok'
                                    : p.decision?.decision === 'changes'
                                      ? ' badge--warn'
                                      : ''
                                }`}
                              >
                                {p.decision?.decision === 'approved'
                                  ? 'Approved'
                                  : p.decision?.decision === 'changes'
                                    ? 'Changes needed'
                                    : 'Not answered'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {sharing && brand && (
        <ShareForReview
          brand={brand}
          postIds={chosen}
          onClose={() => {
            setSharing(false);
            setChosen([]);
            load();
          }}
        />
      )}
    </>
  );
}
